import { useState, useRef, useEffect, useCallback } from 'react'
import { Bot, Send, X, Sparkles, CornerDownLeft } from 'lucide-react'
import { useFocusTrap } from '@gjl/ui'
import { cn } from '@/lib/cn'
import { AI_KNOWLEDGE, SUGGESTED_QUESTIONS } from '@/data/ai-knowledge'

/* ============================================================================
 * AskGarrettAI — the grounded-assistant demo on this site.
 *
 * This widget is a small grounded assistant: by default it does keyword
 * retrieval over a verified fact corpus (a demo, not a live model call). The
 * optional wiring point below sends the question + that corpus as grounding
 * context to a Bedrock Claude backend if VITE_AI_AGENT_URL is set.
 *
 *   ┌──────────────────────────────────────────────────────────────────────┐
 *   │  WIRING POINT — to make this a real LLM call:                          │
 *   │                                                                        │
 *   │  Set VITE_AI_AGENT_URL to a Lambda+API-Gateway endpoint that accepts   │
 *   │    { question: string, history: Msg[] }                                │
 *   │  and returns { answer: string } (or streams text/event-stream).        │
 *   │                                                                        │
 *   │  That Lambda should call Bedrock (Claude Sonnet/Haiku) with the        │
 *   │  AI_KNOWLEDGE corpus as grounding context and a system prompt that      │
 *   │  pins it to ONLY answer from that corpus — the same grounding-gate      │
 *   │  discipline used in the leadgen system. Reuse the finboard              │
 *   │  capture-assistant Lambda pattern (self-invoke for >30s turns).         │
 *   │                                                                        │
 *   │  Until that env var is set, the widget runs the local retrieval        │
 *   │  fallback below — which gives real, accurate answers offline.          │
 *   └──────────────────────────────────────────────────────────────────────┘
 * ========================================================================== */

const AGENT_BACKEND_URL = import.meta.env.VITE_AI_AGENT_URL as string | undefined

interface Msg {
  role: 'user' | 'assistant'
  content: string
}

const GREETING =
  "Hi — I'm the grounded assistant on this site. Ask me anything about Garrett's AI systems, the architecture behind them, or how he stays on the defended side of the AI line. I retrieve from a verified fact corpus, so I'll tell you if I don't have it. (Demo mode — keyword retrieval, not a live model call.)"

/** Lightweight keyword retrieval over the verified corpus (local fallback). */
function retrieveAnswer(question: string): string {
  const q = question.toLowerCase()
  let best: { score: number; answer: string } | null = null

  for (const entry of AI_KNOWLEDGE) {
    let score = 0
    for (const topic of entry.topics) {
      if (q.includes(topic)) score += topic.length // longer matches weigh more
    }
    if (score > 0 && (!best || score > best.score)) {
      best = { score, answer: entry.answer }
    }
  }

  if (best) return best.answer

  return "I retrieve from a verified fact corpus about Garrett's production AI work, and I don't have a confident answer to that one. Try asking about the lead-gen agent fleet, the finboard doctrine system, how he cut Bedrock cost, or whether he's resilient to AI eating software jobs. Or just email garrett@garrettjohnlaw.com."
}

/** Calls the real backend if configured; otherwise resolves the local answer. */
async function askAgent(question: string, history: Msg[]): Promise<string> {
  if (AGENT_BACKEND_URL) {
    try {
      const res = await fetch(AGENT_BACKEND_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, history }),
      })
      if (!res.ok) throw new Error(`agent ${res.status}`)
      const data = (await res.json()) as { answer?: string }
      if (data.answer) return data.answer
    } catch {
      // fall through to local retrieval so the demo never goes dark
    }
  }
  // Local fallback — small latency so the "thinking" state is visible.
  await new Promise((r) => setTimeout(r, 450 + Math.random() * 400))
  return retrieveAnswer(question)
}

export function AskGarrettAI() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Msg[]>([{ role: 'assistant', content: GREETING }])
  const [input, setInput] = useState('')
  const [thinking, setThinking] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const trapRef = useFocusTrap<HTMLDivElement>(open, { onEscape: () => setOpen(false) })

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, thinking])

  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  const send = useCallback(
    async (text: string) => {
      const question = text.trim()
      if (!question || thinking) return

      const history = messages
      setMessages((m) => [...m, { role: 'user', content: question }])
      setInput('')
      setThinking(true)

      const answer = await askAgent(question, history)
      setThinking(false)
      setMessages((m) => [...m, { role: 'assistant', content: answer }])
    },
    [messages, thinking]
  )

  return (
    <>
      {/* Launcher */}
      <button
        type="button"
        aria-label={open ? 'Close the AI assistant' : 'Ask the AI assistant about Garrett'}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'fixed right-5 bottom-5 z-[60] flex items-center gap-2 rounded-full px-4 py-3',
          'gradient-cyber text-primary-foreground font-semibold shadow-lg',
          'hover:glow-cyber-sm transition-all duration-300 hover:-translate-y-0.5',
          'focus-visible:ring-primary focus-visible:ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
        )}
      >
        {open ? <X className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
        <span className="hidden text-sm sm:inline">{open ? 'Close' : 'Ask the AI'}</span>
      </button>

      {/* Panel */}
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-label="AI assistant"
        aria-hidden={!open}
        className={cn(
          'fixed right-3 bottom-20 z-[60] w-[min(24rem,calc(100vw-1.5rem))] sm:right-5',
          'glass-card border-primary/20 overflow-hidden rounded-2xl border shadow-2xl',
          'flex origin-bottom-right flex-col transition-all duration-300',
          open
            ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
            : 'pointer-events-none translate-y-4 scale-95 opacity-0'
        )}
        style={{ height: 'min(34rem, calc(100vh - 7rem))' }}
      >
        {/* Header */}
        <div className="border-border/60 bg-background/40 flex items-center gap-3 border-b px-4 py-3">
          <div className="gradient-cyber flex h-9 w-9 items-center justify-center rounded-lg">
            <Bot className="text-primary-foreground h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-foreground text-sm leading-tight font-semibold">Ask about Garrett’s work</p>
            <p className="text-muted-foreground text-[11px] leading-tight">
              Grounded assistant · retrieval over verified facts
            </p>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4" data-lenis-prevent>
          {messages.map((m, i) => (
            <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
              <div
                className={cn(
                  'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
                  m.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-br-sm'
                    : 'bg-secondary/70 text-foreground border-border/50 rounded-bl-sm border'
                )}
              >
                {m.content}
              </div>
            </div>
          ))}

          {thinking && (
            <div className="flex justify-start">
              <div className="border-border/50 bg-secondary/70 flex items-center gap-1.5 rounded-2xl rounded-bl-sm border px-4 py-3">
                <span className="bg-primary h-2 w-2 animate-bounce rounded-full [animation-delay:-0.3s]" />
                <span className="bg-primary h-2 w-2 animate-bounce rounded-full [animation-delay:-0.15s]" />
                <span className="bg-primary h-2 w-2 animate-bounce rounded-full" />
              </div>
            </div>
          )}

          {/* Suggested questions (only before the first user turn) */}
          {messages.length === 1 && !thinking && (
            <div className="space-y-2 pt-1">
              {SUGGESTED_QUESTIONS.slice(0, 4).map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => send(q)}
                  className="border-primary/20 bg-primary/5 text-foreground/90 hover:border-primary/50 hover:bg-primary/10 block w-full rounded-xl border px-3 py-2 text-left text-xs transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Composer */}
        <form
          onSubmit={(e) => {
            e.preventDefault()
            send(input)
          }}
          className="border-border/60 bg-background/40 border-t p-3"
        >
          <div className="border-border bg-input/60 focus-within:border-primary/60 flex items-center gap-2 rounded-xl border px-3 py-2">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about the AI systems…"
              className="text-foreground placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-sm focus:outline-none"
              aria-label="Your question"
            />
            <button
              type="submit"
              disabled={!input.trim() || thinking}
              aria-label="Send"
              className="gradient-cyber text-primary-foreground flex h-7 w-7 items-center justify-center rounded-lg transition-opacity disabled:opacity-40"
            >
              <Send className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="text-muted-foreground mt-1.5 flex items-center gap-1 px-1 text-[10px]">
            <CornerDownLeft className="h-3 w-3" />
            {AGENT_BACKEND_URL ? 'Live Bedrock agent' : 'Demo mode · retrieval over verified facts'}
          </p>
        </form>
      </div>
    </>
  )
}
